import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PieChart as PieIcon, ArrowRight, Loader2, Rewind } from "lucide-react";
import { PieChart, Pie, Cell, Legend, ResponsiveContainer, Tooltip } from "recharts";
import { api, fmtKES } from "@/lib/api";
import { signTone, toneClassName, signedPrefix } from "@/lib/numberColor";
import GlobalBackButton from "@/components/GlobalBackButton";
import { useSortableData } from "@/hooks/useSortableData";
import { SortableTh } from "@/components/ui/sortable-th";

/**
 * Portfolio Optimizer page — renders MVO output from Obondo P2.
 *
 * Shows two side-by-side pies (Current vs Target), a rebalance-delta table,
 * and a "Rebalance now" button that batches the necessary buys & sells.
 *
 * Executes rebalance via `POST /obondo/autopilot/execute` per delta so each
 * trade goes through the P3 guardrails (kill-switch, sector cap, SL/TP auto-attach).
 */
const PALETTE = ["#4f46e5", "#0ea5e9", "#f59e0b", "#10b981", "#ef4444", "#a855f7", "#14b8a6", "#f43f5e"];

export default function PortfolioSuggestion() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [rebalancing, setRebalancing] = useState(false);
    const [lambdaRisk, setLambdaRisk] = useState(3.0);

    async function loadSuggestion() {
        setBusy(true);
        try {
            const { data } = await api.get(`/obondo/autopilot/portfolio-suggestion?lambda_risk=${lambdaRisk}`);
            setData(data);
        } catch (e) {
            const msg = e?.response?.data?.detail || e.message;
            toast.error(`Suggestion unavailable: ${msg}`);
        } finally {
            setBusy(false);
        }
    }

    useEffect(() => { loadSuggestion(); /* eslint-disable-next-line */ }, []);

    async function rebalanceOne(sym, delta) {
        const side = delta > 0 ? "buy" : "sell";
        const t = await api.get(`/obondo/autopilot/recommendation/${sym}`).catch(() => null);
        const price = t?.data?.price;
        if (!price) return { sym, ok: false, reason: "no price" };
        const qty = Math.max(1, Math.floor(Math.abs(delta) / price));
        try {
            await api.post("/obondo/autopilot/execute", {
                symbol: sym, side, qty,
                stop_loss: t?.data?.stop_loss || null,
                take_profit: t?.data?.take_profit || null,
                confidence: 0.60, source: "semi_auto",
            });
            return { sym, ok: true, qty };
        } catch (e) {
            const reason = e?.response?.data?.detail?.reason || e?.response?.data?.detail || "rejected";
            return { sym, ok: false, reason };
        }
    }

    async function rebalanceAll() {
        if (!data?.deltas) return;
        setRebalancing(true);
        const results = [];
        for (const [sym, d] of Object.entries(data.deltas)) {
            if (Math.abs(d.delta_kes) < 500) continue;   // skip trivial deltas
            const r = await rebalanceOne(sym, d.delta_kes);
            results.push(r);
        }
        const ok = results.filter((r) => r.ok).length;
        toast.success(`Rebalance complete — ${ok}/${results.length} orders filled`);
        loadSuggestion();
        setRebalancing(false);
    }

    // Derive current open-position symbols (weight > 0 in current allocation)
    // so we can offer a "Backtest my current portfolio" shortcut that pre-fills
    // the basket picker on /app/backtest with the exact tickers the user holds.
    const openSymbols = data
        ? Object.entries(data.deltas || {})
            .filter(([, d]) => (d?.current_kes || 0) > 0)
            .map(([s]) => s)
        : [];

    function backtestCurrentPortfolio() {
        if (openSymbols.length === 0) {
            toast.info("No open positions yet — buy something first, then come back.");
            return;
        }
        const basket = openSymbols.join(",");
        navigate(`/app/backtest?basket=${encodeURIComponent(basket)}`);
    }

    const deltaRows = Object.entries(data?.deltas || {}).map(([sym, d]) => ({ sym, ...d }));
    const { sorted: sortedDeltas, sortKey, direction, requestSort } = useSortableData(deltaRows, {
        accessors: {
            symbol: (r) => r.sym || "",
            target: (r) => Number(r.target_kes || 0),
            current: (r) => Number(r.current_kes || 0),
            delta: (r) => Number(r.delta_kes || 0),
            action: (r) => r.action || "",
        },
    });

    if (busy) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
        );
    }
    if (!data) return null;

    const assets = data.assets || {};
    const symbols = Object.keys(assets);
    // Build pie datasets
    const targetPie = symbols
        .filter((s) => assets[s].weight > 0.001)
        .map((s, i) => ({ name: s, value: Math.round(assets[s].weight * 10000) / 100, fill: PALETTE[i % PALETTE.length] }));
    const equity = data.equity_kes || 0;
    const currentPie = Object.entries(data.deltas || {})
        .filter(([, d]) => d.current_kes > 0)
        .map(([s, d], i) => ({ name: s, value: Math.round(d.current_kes / Math.max(equity, 1) * 10000) / 100, fill: PALETTE[i % PALETTE.length] }));

    return (
        <div className="min-h-screen bg-background text-foreground p-4 sm:p-6" data-testid="portfolio-suggestion-page">
            <GlobalBackButton />
            <div className="max-w-6xl mx-auto space-y-6">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                        <PieIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                        <div className="overline text-primary">Obondo Autopilot · Portfolio Optimizer</div>
                        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Rebalance to target weights</h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Max-Sharpe portfolio computed from 60-bar log-returns across your holdings plus a core NSE
                            diversification basket. Long-only. {data.meta?.note}
                        </p>
                    </div>
                </div>

                <div className="border border-border bg-card rounded-lg p-4 flex items-center gap-4 flex-wrap">
                    <div>
                        <label className="text-xs font-semibold text-muted-foreground">Risk aversion (λ)</label>
                        <input type="range" min={1} max={10} step={0.5} value={lambdaRisk}
                            onChange={(e) => setLambdaRisk(Number(e.target.value))}
                            className="ml-2 accent-primary" data-testid="portfolio-lambda" />
                        <span className="ml-2 mono text-sm">{lambdaRisk}</span>
                    </div>
                    <button onClick={loadSuggestion} className="h-8 px-3 rounded border border-border hover:bg-muted text-sm" data-testid="portfolio-recompute">
                        Recompute
                    </button>
                    <button
                        onClick={backtestCurrentPortfolio}
                        disabled={openSymbols.length === 0}
                        className="h-8 px-3 rounded border border-primary/60 text-primary hover:bg-primary/10 text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="portfolio-backtest-shortcut"
                        title={openSymbols.length === 0
                            ? "Open at least one position to enable this shortcut"
                            : `Pre-fill /app/backtest with ${openSymbols.length} open position(s)`}
                    >
                        <Rewind className="w-3.5 h-3.5" />
                        Backtest my current portfolio
                        {openSymbols.length > 0 && (
                            <span className="ml-1 text-[10px] mono opacity-70">({openSymbols.length})</span>
                        )}
                    </button>
                    <div className="ml-auto text-xs text-muted-foreground">
                        Portfolio Sharpe: <span className="mono font-bold text-foreground">{data.meta?.portfolio_sharpe}</span>
                        {" · "}Vol: <span className="mono font-bold text-foreground">{data.meta?.portfolio_vol}</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <PieCard title={`Current allocation · ${fmtKES(equity)}`} pie={currentPie} empty="No open positions yet." />
                    <PieCard title="Target allocation · max-Sharpe" pie={targetPie} empty="No positive-EV assets in universe." />
                </div>

                <div className="border border-border bg-card rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <h3 className="font-semibold text-sm">Rebalance deltas</h3>
                        <button
                            onClick={rebalanceAll}
                            disabled={rebalancing || !data.deltas || Object.keys(data.deltas).length === 0}
                            className="px-3 py-1.5 rounded bg-primary text-primary-foreground text-xs font-bold hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                            data-testid="portfolio-rebalance-btn"
                        >
                            {rebalancing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Rebalancing…</> : <>Rebalance all <ArrowRight className="w-3.5 h-3.5" /></>}
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs" data-testid="portfolio-deltas-table">
                            <thead className="border-b border-border">
                                <tr className="text-left text-muted-foreground">
                                    <SortableTh label="Symbol" sortKey="symbol" currentKey={sortKey} direction={direction} onSort={requestSort} className="py-2 pr-2" />
                                    <SortableTh label="Target" sortKey="target" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" className="pr-2" />
                                    <SortableTh label="Current" sortKey="current" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" className="pr-2" />
                                    <SortableTh label="Delta" sortKey="delta" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" className="pr-2" />
                                    <th className="pr-2">Expected μ</th>
                                    <SortableTh label="Action" sortKey="action" currentKey={sortKey} direction={direction} onSort={requestSort} />
                                </tr>
                            </thead>
                            <tbody>
                                {sortedDeltas.map((d) => {
                                    const sym = d.sym;
                                    const asset = assets[sym] || {};
                                    return (
                                        <tr key={sym} className="border-b border-border/50 last:border-0">
                                            <td className="py-1.5 pr-2 font-bold mono">{sym}</td>
                                            <td className="pr-2 text-right mono">{fmtKES(d.target_kes)}</td>
                                            <td className="pr-2 text-right mono">{fmtKES(d.current_kes)}</td>
                                            <td className={`pr-2 text-right mono font-semibold ${toneClassName(signTone(d.delta_kes))}`}>
                                                {signedPrefix(d.delta_kes)}{fmtKES(d.delta_kes)}
                                            </td>
                                            <td className="pr-2 mono">{(asset.expected_return * 100 || 0).toFixed(3)}%</td>
                                            <td className={`font-bold ${d.action === "buy" ? "text-emerald-500" : d.action === "sell" ? "text-red-500" : "text-muted-foreground"}`}>
                                                {d.action.toUpperCase()}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

function PieCard({ title, pie, empty }) {
    return (
        <div className="border border-border bg-card rounded-lg p-4">
            <h3 className="font-semibold text-sm mb-2">{title}</h3>
            <div className="h-56">
                {pie.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-xs text-muted-foreground">{empty}</div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie data={pie} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={(e) => `${e.name} ${e.value}%`}>
                                {pie.map((e) => <Cell key={e.name} fill={e.fill} />)}
                            </Pie>
                            <Tooltip formatter={(v) => `${v}%`} />
                        </PieChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
}
